import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { adminLegalDocumentsQueryKey } from "@/lib/admin-queries";
import { adminApi } from "@/lib/api";
import { formatDate } from "@/lib/format";

// The "Dokumenty prawne" section (#305): the two versioned legal documents with
// their published/draft state. Each row opens the markdown editor for that slug.
// A flat, hairline-bordered list (not a card grid) per DESIGN.md — the tab is a
// quiet ops surface, coral reserved for hover/active.
function LegalDocumentsRoute() {
  const query = useQuery({
    queryKey: adminLegalDocumentsQueryKey(),
    queryFn: () => adminApi.legalDocuments.list(),
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Dokumenty prawne</h1>
        <p className="text-muted-foreground">
          Regulamin i Polityka prywatności. Edytuj w Markdown, a publikacja tworzy nową, niezmienną
          wersję widoczną na stronie.
        </p>
      </header>

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">Nie udało się wczytać dokumentów.</p>
      ) : (
        <ul className="divide-y rounded-xl border bg-surface">
          {query.data.documents.map((doc) => (
            <li key={doc.slug}>
              <Link
                to="/legal-documents/$slug"
                params={{ slug: doc.slug }}
                className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-accent"
              >
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="font-medium">{doc.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {doc.latestVersion !== null && doc.publishedAt !== null
                      ? `Opublikowano wersję ${doc.latestVersion} · ${formatDate(doc.publishedAt)}`
                      : "Jeszcze nieopublikowany"}
                  </p>
                </div>
                {doc.hasDraft ? (
                  <Badge
                    className="bg-primary/10 text-primary ring-primary/20"
                    title="Zapisany szkic z niepublikowanymi zmianami"
                  >
                    Szkic
                  </Badge>
                ) : null}
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/legal-documents/")({ component: LegalDocumentsRoute });
